import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { SEOTags } from '@/components/SEOTags'

export default function SecurityPage() {
    useEffect(() => {
        window.scrollTo(0, 0)
    }, [])

    const breadcrumbSchema = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://steadfasthsa.com" },
            { "@type": "ListItem", "position": 2, "name": "Security", "item": "https://steadfasthsa.com/security" }
        ]
    });

    return (
        <div className="min-h-screen bg-slate-50 font-sans selection:bg-teal-100 selection:text-teal-900 flex flex-col">
            <SEOTags
                title="Security | Steadfast HSA"
                description="Steadfast HSA takes the security of your data seriously. Learn about our robust security measures, encryption practices, and commitment to protecting your sensitive information."
                url="https://steadfasthsa.com/security"
                schema={breadcrumbSchema}
            />
            {/* Header */}
            <nav className="bg-white border-b border-gray-200 py-4 sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
                    <Link to="/" className="text-2xl font-bold tracking-tight text-navy">
                        STEADFAST <span className="text-gold">HSA</span>
                    </Link>
                    <Link
                        to="/"
                        className="text-sm font-semibold text-slate-600 hover:text-navy transition"
                    >
                        ← Back to Home
                    </Link>
                </div>
            </nav>

            {/* Content */}
            <main className="max-w-4xl mx-auto px-6 py-16">
                <div className="text-center mb-12">
                    <h1 className="text-4xl font-extrabold text-navy mb-2">Security</h1>
                    <p className="text-slate-500">Last updated: March 1, 2026</p>
                </div>

                <div className="prose prose-slate prose-lg max-w-none
          prose-headings:text-navy prose-headings:font-bold
          prose-h1:text-3xl prose-h1:mt-12 prose-h1:mb-6
          prose-h2:text-xl prose-h2:mt-8 prose-h2:mb-4
          prose-a:text-blue-600 prose-a:underline
          prose-strong:text-navy
          prose-table:border-collapse prose-th:bg-gray-50 prose-th:p-3 prose-th:text-left prose-td:p-3 prose-td:border-t prose-td:border-gray-200
        ">
                    <div className="bg-navy rounded-xl p-6 mb-10">
                        <p className="font-bold text-gold mb-2">OUR SECURITY COMMITMENTS</p>
                        <p className="text-white text-sm leading-relaxed">
                            Your receipts contain health information. We treat them accordingly. All data is encrypted in transit and at rest. We never store passwords. Authentication is handled by a dedicated, specialized provider. Your data is isolated at the database level — no user can access another's records. We will notify you promptly if your data is ever at risk.
                        </p>
                    </div>

                    <p>
                        Steadfast HSA is designed for one purpose: to be the long-term vault for your HSA documentation. You may store receipts for 20–30 years before you ever need them. That timeline demands infrastructure and security practices built to last — not corners cut to ship faster.
                    </p>
                    <p>
                        This page describes our security posture in plain terms. We name our infrastructure providers, describe our controls, and are honest about where we are on the security maturity roadmap.
                    </p>

                    <h1 id="data-in-transit">1. Data in Transit</h1>
                    <p>
                        All communication between your browser, our application, and our infrastructure is encrypted using TLS 1.2 or higher. We enforce HTTPS across all endpoints — there is no unencrypted fallback. HTTP requests are automatically redirected to HTTPS.
                    </p>
                    <p>
                        Our frontend is served through Cloudflare's global network, which terminates TLS at the edge, providing both encryption and DDoS mitigation. The HTTP Strict Transport Security (HSTS) header is set on all responses with a two-year max-age and preload registration, instructing browsers to never attempt an unencrypted connection to our domains.
                    </p>

                    <h1 id="data-at-rest">2. Data at Rest</h1>

                    <h2>Expense Records &amp; Account Data</h2>
                    <p>
                        All structured data — your expense records, account metadata, and family member information — is stored in a managed PostgreSQL database hosted by Neon. Neon encrypts all data at rest and maintains SOC 2 Type II compliance. Backups are encrypted and retained for 30 days.
                    </p>

                    <h2>Receipt Files &amp; Documents</h2>
                    <p>
                        Your uploaded files (receipt images, PDFs, and exported archives) are stored in Cloudflare R2, Cloudflare's object storage product. R2 encrypts all objects at rest using AES-256. Files are accessed only through short-lived, signed URLs generated on demand — there are no permanent public links to your documents. Cloudflare's zero-egress architecture means your files do not traverse third-party networks for delivery.
                    </p>
                    <p>
                        File storage paths include your account identifier but not personally identifiable information. Even if a storage path were exposed, it would not be directly usable without a valid signed URL.
                    </p>

                    <h1 id="authentication">3. Authentication &amp; Account Security</h1>
                    <p>
                        Authentication is handled entirely by <strong>Clerk</strong>, a dedicated identity and authentication platform. We do not store passwords. We never see your password — it is handled, hashed, and stored by Clerk using industry-standard practices.
                    </p>
                    <p>
                        Clerk provides:
                    </p>
                    <ul>
                        <li><strong>Multi-factor authentication (MFA):</strong> TOTP authenticator app support. We strongly recommend enabling MFA on your account.</li>
                        <li><strong>Social sign-on:</strong> Optional sign-in via Google and Apple, using those providers' authentication flows.</li>
                        <li><strong>Session management:</strong> Active sessions are listed in your account settings. You can revoke any session remotely at any time.</li>
                        <li><strong>Credential breach detection:</strong> Clerk monitors for compromised credentials and flags accounts accordingly.</li>
                    </ul>
                    <p>
                        All API requests from the frontend include a short-lived JSON Web Token (JWT) issued by Clerk. Our backend validates the token signature on every request. Tokens are never stored in our database.
                    </p>

                    <h1 id="data-isolation">4. Data Isolation</h1>
                    <p>
                        Every database query in the application is scoped to the authenticated user's account ID. It is not possible for one user to retrieve, modify, or delete another user's expenses, receipts, or account data — this is enforced at the application layer, not just the UI layer.
                    </p>
                    <p>
                        Receipt files in storage are namespaced by account identifier. Access to files requires both a valid authenticated session and ownership of the associated account.
                    </p>

                    <h1 id="browser-security">5. Browser &amp; Transport Security Headers</h1>
                    <p>
                        Every response from our application includes security headers designed to protect against common browser-based attacks:
                    </p>
                    <div className="overflow-x-auto">
                        <table>
                            <thead>
                                <tr>
                                    <th>Header</th>
                                    <th>Value</th>
                                    <th>Purpose</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><strong>Strict-Transport-Security</strong></td>
                                    <td>max-age=63072000; includeSubDomains; preload</td>
                                    <td>Forces HTTPS for 2 years; prevents SSL stripping</td>
                                </tr>
                                <tr>
                                    <td><strong>X-Frame-Options</strong></td>
                                    <td>DENY</td>
                                    <td>Prevents clickjacking via iframes</td>
                                </tr>
                                <tr>
                                    <td><strong>X-Content-Type-Options</strong></td>
                                    <td>nosniff</td>
                                    <td>Prevents MIME-type sniffing attacks</td>
                                </tr>
                                <tr>
                                    <td><strong>Referrer-Policy</strong></td>
                                    <td>strict-origin-when-cross-origin</td>
                                    <td>Limits referrer data sent to third parties</td>
                                </tr>
                                <tr>
                                    <td><strong>Permissions-Policy</strong></td>
                                    <td>camera=(), microphone=(), geolocation=()</td>
                                    <td>Blocks access to device hardware APIs</td>
                                </tr>
                                <tr>
                                    <td><strong>Content-Security-Policy</strong></td>
                                    <td>Strict allowlist</td>
                                    <td>Restricts scripts, styles, and resources to known origins; blocks XSS</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <h1 id="infrastructure">6. Infrastructure &amp; Hosting</h1>
                    <p>
                        We deliberately chose infrastructure providers with strong, independently verified security postures. We do not run our own physical servers.
                    </p>
                    <div className="overflow-x-auto">
                        <table>
                            <thead>
                                <tr>
                                    <th>Component</th>
                                    <th>Provider</th>
                                    <th>Security Posture</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><strong>Frontend &amp; CDN</strong></td>
                                    <td>Cloudflare Pages</td>
                                    <td>Global edge network, automatic HTTPS, DDoS protection, WAF</td>
                                </tr>
                                <tr>
                                    <td><strong>File Storage</strong></td>
                                    <td>Cloudflare R2</td>
                                    <td>AES-256 encryption at rest, zero-egress, signed URL access only</td>
                                </tr>
                                <tr>
                                    <td><strong>Database</strong></td>
                                    <td>Neon (PostgreSQL)</td>
                                    <td>SOC 2 Type II, encryption at rest, automated backups</td>
                                </tr>
                                <tr>
                                    <td><strong>Backend API</strong></td>
                                    <td>Railway</td>
                                    <td>Isolated container execution, private networking, no public database exposure</td>
                                </tr>
                                <tr>
                                    <td><strong>Authentication</strong></td>
                                    <td>Clerk</td>
                                    <td>SOC 2 Type II, dedicated identity platform, MFA support</td>
                                </tr>
                                <tr>
                                    <td><strong>Payments</strong></td>
                                    <td>Stripe</td>
                                    <td>PCI-DSS Level 1 certified; we never receive or store card numbers</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <p>
                        Our backend API is not directly exposed to the public internet on a database port. The database accepts connections only from the application server's private network. There is no direct external database access.
                    </p>

                    <h1 id="access-controls">7. Internal Access Controls</h1>
                    <p>
                        Access to production systems is restricted to authorized personnel only. We apply a principle of least privilege — individuals have access only to the systems required for their role. Production database access is not a routine developer privilege.
                    </p>
                    <p>
                        We do not have a large team with complex access sprawl. As a small, bootstrapped company, the surface area for insider risk is limited. As we grow, we will implement formal access review processes and maintain an audit log of production access.
                    </p>

                    <h1 id="incident-response">8. Incident Response</h1>
                    <p>
                        In the event of a confirmed security incident affecting your data, we will:
                    </p>
                    <ul>
                        <li>Notify affected users by email within 72 hours of confirming a breach that affects their data.</li>
                        <li>Describe what data was affected, the scope of the incident, and the steps we are taking to contain and remediate it.</li>
                        <li>Comply with applicable breach notification requirements, including the FTC Health Breach Notification Rule, which we believe applies to our handling of health-adjacent receipt data.</li>
                        <li>Publish a post-incident summary for material incidents once the situation is resolved.</li>
                    </ul>
                    <p>
                        To report a suspected security vulnerability, please contact us at <a href="mailto:security@steadfasthsa.com">security@steadfasthsa.com</a>. We take all reports seriously and will respond within 2 business days. We ask that you give us reasonable time to investigate and remediate before public disclosure.
                    </p>

                    <h1 id="honest-roadmap">9. Where We Are on the Security Roadmap</h1>
                    <p>
                        We believe in being honest about what we have done and what we have not yet done. The following items are on our security roadmap but are not yet complete:
                    </p>
                    <ul>
                        <li><strong>SOC 2 Type II (Steadfast HSA):</strong> We rely on our infrastructure providers' SOC 2 certifications today. Our own SOC 2 audit is a planned milestone as the business scales.</li>
                        <li><strong>Bug bounty program:</strong> Planned. In the interim, we accept responsible disclosures at <a href="mailto:security@steadfasthsa.com">security@steadfasthsa.com</a>.</li>
                    </ul>
                    <p>
                        We will update this page as these milestones are reached.
                    </p>

                    <h1 id="contact">10. Contact</h1>
                    <p>For security inquiries, vulnerability reports, or questions about our security practices:</p>
                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 my-8">
                        <p className="font-bold text-navy mb-2">Steadfast Financial, LLC — Security Team</p>
                        <p className="text-slate-600 text-sm">
                            Security Reports &amp; Vulnerabilities: <a href="mailto:security@steadfasthsa.com">security@steadfasthsa.com</a><br />
                            Privacy Inquiries: <a href="mailto:privacy@steadfasthsa.com">privacy@steadfasthsa.com</a><br />
                            General Contact: <a href="mailto:hello@steadfasthsa.com">hello@steadfasthsa.com</a>
                        </p>
                    </div>
                </div>

                <div className="mt-16 pt-8 border-t border-gray-200 text-center text-sm text-slate-400">
                    STEADFAST HSA &nbsp;|&nbsp; Security &nbsp;|&nbsp; Last updated March 1, 2026
                </div>
            </main>

            {/* Footer */}
            <footer className="py-12 bg-white border-t border-gray-200">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="flex flex-col md:flex-row justify-between items-center text-sm text-slate-500">
                        <div className="mb-6 md:mb-0">
                            <span className="font-bold text-navy">STEADFAST HSA</span> © {new Date().getFullYear()}. Built for the long term.
                        </div>
                        <div className="flex flex-wrap justify-center gap-6 md:gap-8">
                            <Link to="/" className="hover:text-navy transition">Home</Link>
                            <Link to="/privacy" className="hover:text-navy transition">Privacy Policy</Link>
                            <Link to="/terms" className="hover:text-navy transition font-semibold text-navy">Terms of Service</Link>
                            <Link to="/security" className="hover:text-navy transition font-semibold text-navy">Security</Link>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    )
}
